// components/SidebarHeader.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { APP_CONFIG } from '../app-config';
import AppIcon from "../assets/image/icon.png";

// Import React Icons
import {
    HiHome,
    HiCreditCard,
    HiCash,
    HiSupport,
    HiBell,
    HiMenu,
    HiX,
    HiChevronDown,
    HiLogout
} from 'react-icons/hi';
import {
    MdDashboard,
    MdReceipt,
    MdAddCircle,
    MdSupportAgent,
    MdNotifications,
    MdMenu,
    MdClose,
    MdExpandMore,
    MdExitToApp
} from 'react-icons/md';
import {
    FiHome,
    FiCreditCard,
    FiDollarSign,
    FiHelpCircle,
    FiBell,
    FiMenu,
    FiX,
    FiChevronDown,
    FiLogOut
} from 'react-icons/fi';

const COLORS = APP_CONFIG.colors;

// Navigation items with dropdown support
const navItems = [
    {
        label: 'Dashboard',
        icon: <HiHome className="w-5 h-5" />,
        path: '/home',
        dropdown: false
    },
    {
        label: 'Recharge & Bills',
        icon: <HiCreditCard className="w-5 h-5" />,
        path: '/services',
        dropdown: false
    },
    {
        label: 'Addmoney',
        icon: <HiCash className="w-5 h-5" />,
        path: '/add-money',
        dropdown: false
    },
    {
        label: 'History',
        icon: <MdReceipt className="w-5 h-5" />,
        path: '/history',
        dropdown: false
    },
    {
        label: 'Margin',
        icon: <FiDollarSign className="w-5 h-5" />,
        path: '/margin',
        dropdown: false
    },
    {
        label: 'Support',
        icon: <HiSupport className="w-5 h-5" />,
        path: '/support-center',
        dropdown: false
    }
];

const SidebarHeader = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [openDropdown, setOpenDropdown] = useState(null);
    const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
    const sidebarRef = useRef(null);

    //user profile
    const storedUserData = localStorage.getItem("credentials");
    const parsedData = JSON.parse(storedUserData);
    const username = parsedData?.name || '';
    const userEmail = parsedData?.email || '';

    // Handle window resize
    useEffect(() => {
        const handleResize = () => {
            const desktop = window.innerWidth >= 1024;
            setIsDesktop(desktop);
            if (desktop) {
                setIsSidebarOpen(true);
            } else {
                setIsSidebarOpen(false);
            }
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Close sidebar on route change (mobile only)
    useEffect(() => {
        if (!isDesktop) {
            setIsSidebarOpen(false);
            setOpenDropdown(null);
        }
    }, [location.pathname, isDesktop]);

    // Close sidebar when clicking outside (mobile only)
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!isDesktop && isSidebarOpen && sidebarRef.current && !sidebarRef.current.contains(event.target)) {
                setIsSidebarOpen(false);
                setOpenDropdown(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isDesktop, isSidebarOpen]);

    const toggleSidebar = () => {
        if (!isDesktop) {
            setIsSidebarOpen(!isSidebarOpen);
            if (isSidebarOpen) {
                setOpenDropdown(null);
            }
        }
    };

    const closeSidebar = () => {
        if (!isDesktop) {
            setIsSidebarOpen(false);
            setOpenDropdown(null);
        }
    };

    const toggleDropdown = (index) => {
        setOpenDropdown(openDropdown === index ? null : index);
    };

    const handleNavigation = (path, state = null) => {
        if (path) {
            navigate(path, { state });
            if (!isDesktop) {
                closeSidebar();
            }
        }
    };

    const isActiveRoute = (path) => {
        return location.pathname === path;
    };

    const isDropdownActive = (item) => {
        if (!item.dropdownItems) return false;
        return item.dropdownItems.some(dropItem =>
            location.pathname === dropItem.path
        );
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Top Header */}
            <header
                className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm"
                style={{
                    borderBottom: `1px solid ${COLORS.borderColor}`,
                }}
            >
                <div className="px-4 h-16 flex items-center justify-between" style={{ maxWidth: '1600px', margin: '0 auto' }}>
                    {/* Left Section - Now contains both logo and user profile */}
                    <div className="flex items-center gap-4">
                        {/* Mobile Menu Button */}
                        <button
                            onClick={toggleSidebar}
                            className={`p-2 rounded-lg hover:bg-gray-100 transition-colors lg:hidden ${!isDesktop ? 'block' : 'hidden'
                                }`}
                            style={{ color: COLORS.textPrimary }}
                        >
                            <HiMenu className="w-6 h-6" />
                        </button>

                        {/* Logo */}
                        <div
                            className="text-xl font-bold cursor-pointer flex items-center gap-2"
                            style={{ color: COLORS.primaryColor }}
                            onClick={() => handleNavigation('/home')}
                        >
                            <img
                                src={AppIcon}
                                alt="App Icon"
                                className="rounded-xl w-8 h-8 sm:w-10 sm:h-10 object-contain"
                            />
                            {APP_CONFIG.app.name}
                        </div>
                    </div>

                    {/* Right Section - Only notification */}
                    <div className="flex items-center gap-4">
                        {/* Notification */}
                        <button className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors relative" onClick={() => handleNavigation('/notification')}>
                            <HiBell className="w-5 h-5" style={{ color: COLORS.textSecondary }} />
                        </button>
                        {/* Divider */}
                        <div className="h-8 w-px bg-gray-200 hidden md:block"></div>

                        {/* User Profile - Now on the left side */}
                        <div className="flex items-center gap-3 bg-gray-100 rounded-lg p-2 cursor-pointer" onClick={() => handleNavigation('/profile')}>
                            <button
                                className="w-8 h-8 rounded-full flex items-center justify-center transition-all hover:scale-105 flex-shrink-0"
                                style={{
                                    background: COLORS.primaryColor,
                                    color: COLORS.textOnPrimary,
                                }}
                            >
                                <span className="text-sm font-bold">
                                    {username.charAt(0).toUpperCase()}
                                </span>
                            </button>
                            <div className="hidden sm:block text-left">
                                <div className="text-sm font-medium" style={{ color: COLORS.textPrimary }}>
                                    {username}
                                </div>
                                <div className="text-xs" style={{ color: COLORS.textSecondary }}>
                                    {userEmail}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* Sidebar Overlay (Mobile) */}
            {!isDesktop && isSidebarOpen && (
                <div
                    className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm transition-opacity"
                    onClick={closeSidebar}
                />
            )}

            {/* Sidebar + Main Content */}
            <div className="flex pt-16 min-h-screen">
                {/* Sidebar - Always takes up space in flex layout on desktop */}
                <aside
                    ref={sidebarRef}
                    className={`
                        ${isDesktop ? 'lg:sticky top-16' : 'fixed top-16'}
                        z-40 h-[calc(100vh-64px)] bg-white
                        transition-all duration-300 ease-in-out
                        ${isDesktop ? 'w-72 lg:w-80 flex-shrink-0 relative' : 'w-72'}
                        ${isDesktop ? 'translate-x-0' : isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
                        ${isDesktop ? '' : 'shadow-2xl'}
                    `}
                    style={{
                        borderRight: `1px solid ${COLORS.borderColor}`,
                        overflowY: 'auto',
                    }}
                >
                    {/* Sidebar Content */}
                    <div className="flex flex-col h-full">
                        {/* User Info - Desktop */}
                        {isDesktop && (
                            <div className="p-4 border-b" style={{ borderColor: COLORS.borderColor }}>
                                <div className="flex items-center gap-3">
                                    <div
                                        className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                                        style={{
                                            background: COLORS.primaryColor,
                                            color: COLORS.textOnPrimary,
                                        }}
                                    >
                                        <span className="text-sm font-bold">
                                            {username.charAt(0).toUpperCase()}
                                        </span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium truncate" style={{ color: COLORS.textPrimary }}>
                                            {username}
                                        </div>
                                        <div className="text-xs truncate" style={{ color: COLORS.textSecondary }}>
                                            {userEmail}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* User Info - Mobile */}
                        {!isDesktop && (
                            <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: COLORS.borderColor }}>
                                <div className="flex items-center gap-3">
                                    <div
                                        className="w-12 h-12 rounded-full flex items-center justify-center"
                                        style={{
                                            background: COLORS.primaryColor,
                                            color: COLORS.textOnPrimary,
                                        }}
                                    >
                                        <span className="text-lg font-bold">
                                            {username.charAt(0).toUpperCase()}
                                        </span>
                                    </div>
                                    <div>
                                        <div className="font-medium" style={{ color: COLORS.textPrimary }}>
                                            {username}
                                        </div>
                                        <div className="text-xs" style={{ color: COLORS.textSecondary }}>
                                            {userEmail}
                                        </div>
                                    </div>
                                </div>
                                <button
                                    onClick={closeSidebar}
                                    className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                                    style={{ color: COLORS.textSecondary }}
                                >
                                    <HiX className="w-5 h-5" />
                                </button>
                            </div>
                        )}

                        {/* Navigation */}
                        <nav className="flex-1 p-3 overflow-y-auto">
                            {navItems.map((item, index) => (
                                <div key={index} className="mb-1">
                                    {item.dropdown ? (
                                        <div>
                                            <button
                                                onClick={() => toggleDropdown(index)}
                                                className={`
                                                    w-full flex items-center justify-between px-3 py-2.5 rounded-lg
                                                    transition-all duration-200 hover:bg-gray-50
                                                    ${isDropdownActive(item) ? 'bg-opacity-10' : ''}
                                                `}
                                                style={{
                                                    color: isDropdownActive(item) ? COLORS.primaryColor : COLORS.textPrimary,
                                                    background: isDropdownActive(item) ? `${COLORS.primaryColor}10` : 'transparent',
                                                }}
                                            >
                                                <div className="flex items-center gap-3 min-w-0">
                                                    <span className="flex-shrink-0" style={{ color: isDropdownActive(item) ? COLORS.primaryColor : COLORS.textSecondary }}>
                                                        {item.icon}
                                                    </span>
                                                    <span className="text-sm font-medium truncate">{item.label}</span>
                                                </div>
                                                <HiChevronDown
                                                    className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ${openDropdown === index ? 'rotate-180' : ''
                                                        }`}
                                                    style={{ color: COLORS.textSecondary }}
                                                />
                                            </button>

                                            {openDropdown === index && (
                                                <div className="mt-1 ml-4 space-y-1">
                                                    {item.dropdownItems.map((dropItem, dropIndex) => (
                                                        <button
                                                            key={dropIndex}
                                                            onClick={() => handleNavigation(dropItem.path, dropItem.state)}
                                                            className={`
                                                                w-full text-left px-3 py-2 rounded-lg transition-all duration-200
                                                                hover:bg-gray-50 text-sm
                                                                ${isActiveRoute(dropItem.path) ? 'font-medium' : ''}
                                                            `}
                                                            style={{
                                                                color: isActiveRoute(dropItem.path) ? COLORS.primaryColor : COLORS.textSecondary,
                                                                background: isActiveRoute(dropItem.path) ? `${COLORS.primaryColor}10` : 'transparent',
                                                            }}
                                                        >
                                                            <span className="ml-6">{dropItem.label}</span>
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <button
                                            onClick={() => handleNavigation(item.path)}
                                            className={`
                                                w-full flex items-center gap-3 px-3 py-2.5 rounded-lg
                                                transition-all duration-200 hover:bg-gray-50
                                                ${isActiveRoute(item.path) ? 'bg-opacity-10' : ''}
                                            `}
                                            style={{
                                                color: isActiveRoute(item.path) ? COLORS.primaryColor : COLORS.textPrimary,
                                                background: isActiveRoute(item.path) ? `${COLORS.primaryColor}10` : 'transparent',
                                            }}
                                        >
                                            <span className="flex-shrink-0" style={{ color: isActiveRoute(item.path) ? COLORS.primaryColor : COLORS.textSecondary }}>
                                                {item.icon}
                                            </span>
                                            <span className="text-sm font-medium truncate">{item.label}</span>
                                        </button>
                                    )}
                                </div>
                            ))}

                            {/* Divider */}
                            <div className="my-4 border-t" style={{ borderColor: COLORS.borderColor }} />

                            {/* Logout */}
                            <button
                                onClick={() => {
                                    console.log('Logout clicked');
                                    if (!isDesktop) closeSidebar();
                                }}
                                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 hover:bg-red-50"
                                style={{ color: COLORS.errorColor || '#ef4444' }}
                            >
                                <HiLogout className="w-5 h-5" />
                                <span className="text-sm font-medium">Logout</span>
                            </button>
                        </nav>
                    </div>
                </aside>

                {/* Main Content - No margin needed, flex handles spacing */}
                <main className="flex-1 min-w-0">
                    <div className="p-4 md:p-6">
                        {children}
                    </div>
                </main>
            </div>
        </div>
    );
};

export default SidebarHeader;